import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Scroll, Pin, ExternalLink, PartyPopper, Share2, Check, Bell, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const BOARD_SEEN_KEY = "airpog_board_seen";

const AUDIENCE_BADGE = {
  merchants: ["🏪 MERCHANTS", "text-emerald-300 bg-emerald-500/15 border-emerald-400/40"],
  pogrs: ["🎯 POG'RS", "text-sky-300 bg-sky-500/15 border-sky-400/40"],
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
};

const b64ToUint8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const BoardPost = ({ p, going, onRsvp, canRsvp, onShare, shared, slamNow }) => {
  const badge = AUDIENCE_BADGE[p.audience];
  const isEvent = p.kind === "event";
  const isPogDay = (p.auto_key || "").startsWith("pogday");
  return (
    <div data-testid={`board-post-${p.id}`} className={`rounded-2xl border p-3.5 ${isEvent ? "bg-fuchsia-500/10 border-fuchsia-400/40" : p.pinned ? "bg-amber-500/10 border-amber-400/40" : "bg-slate-800/50 border-slate-700/60"}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-heading font-extrabold text-sm text-white leading-snug">
          {p.pinned && <Pin className="w-3.5 h-3.5 text-amber-400 inline mr-1.5 -mt-0.5" />}
          {p.title}
        </p>
        <span className="flex items-center gap-1.5 shrink-0 mt-0.5">
          <span className="text-[10px] font-bold text-slate-500">{fmtDate(p.created_at)}</span>
          <button data-testid={`board-share-${p.id}`} onClick={() => onShare(p)} title="Share this post"
            className="w-7 h-7 rounded-lg bg-slate-800/70 hover:bg-slate-700 flex items-center justify-center">
            {shared ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Share2 className="w-3.5 h-3.5 text-cyan-300" />}
          </button>
        </span>
      </div>
      <span className="flex flex-wrap items-center gap-1.5 mt-1.5">
        {isEvent && (
          <span data-testid={`board-event-badge-${p.id}`} className="inline-flex items-center gap-1 text-[9px] font-black text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-400/40 rounded-full px-1.5 py-0.5">
            🎉 EVENT{p.event_date ? ` · ${p.event_date}` : ""}
          </span>
        )}
        {badge && <span className={`inline-block text-[9px] font-black border rounded-full px-1.5 py-0.5 ${badge[1]}`}>{badge[0]}</span>}
        {isPogDay && slamNow != null && (
          <span data-testid={`pogday-live-counter-${p.id}`} className={`inline-flex items-center gap-1 text-[9px] font-black rounded-full px-1.5 py-0.5 border ${slamNow > 0 ? "text-red-300 bg-red-500/15 border-red-400/50 animate-pulse" : "text-slate-400 bg-slate-700/40 border-slate-600/50"}`}>
            🥞 {slamNow > 0 ? `${slamNow} SLAMMING RIGHT NOW` : "BE THE FIRST TO SLAM"}
          </span>
        )}
      </span>
      <p className="text-[12px] text-slate-300 font-medium mt-1.5 whitespace-pre-wrap leading-relaxed">{p.body}</p>
      {p.image_url && (
        <img data-testid={`board-post-img-${p.id}`} src={p.image_url} alt={p.title} loading="lazy"
          className="mt-2 w-full max-w-[220px] rounded-xl border-2 border-slate-700/70" />
      )}
      {p.business_name && <p className="text-[10px] font-bold text-slate-500 mt-1">🏪 {p.business_name}</p>}
      {isEvent && (
        <button
          data-testid={`board-rsvp-${p.id}`}
          onClick={() => canRsvp && onRsvp(p.id)}
          disabled={!canRsvp}
          className={`mt-2 h-8 px-3 rounded-full text-[11px] font-heading font-black flex items-center gap-1.5 transition-colors ${
            going ? "bg-fuchsia-500 text-white" : canRsvp ? "bg-fuchsia-500/15 border border-fuchsia-400/50 text-fuchsia-300 hover:bg-fuchsia-500/25" : "bg-slate-800/60 border border-slate-700/60 text-slate-500"
          }`}
          title={canRsvp ? "" : "Sign in to RSVP"}
        >
          <PartyPopper className="w-3.5 h-3.5" />
          {going ? "I'M GOING!" : "I'M GOING"}
          <span className="font-bold opacity-80">· {p.rsvp_count || 0}</span>
        </button>
      )}
      {p.link_url && (
        <a data-testid={`board-post-link-${p.id}`} href={p.link_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-black text-sky-300 mt-2 hover:text-sky-200">
          <ExternalLink className="w-3.5 h-3.5" /> Check it out →
        </a>
      )}
    </div>
  );
};

export const PogBoardSheet = ({ open, onClose, driver, pos }) => {
  const [posts, setPosts] = useState(null);
  const [myRsvps, setMyRsvps] = useState(new Set());
  const [slamNow, setSlamNow] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [radius, setRadius] = useState(() => Number(localStorage.getItem("airpog_push_radius")) || 25);
  const pushSupported = "serviceWorker" in navigator && "PushManager" in window;

  useEffect(() => {
    if (!open || !pushSupported) return;
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription())
      .then((s) => {
        setPushOn(!!s);
        if (!s && !localStorage.getItem("airpog_alert_nudge_done")) setNudge(true);
      }).catch(() => {});
  }, [open, pushSupported]);

  const dismissNudge = () => { localStorage.setItem("airpog_alert_nudge_done", "1"); setNudge(false); };

  const togglePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: existing.endpoint });
        await existing.unsubscribe();
        setPushOn(false);
        return;
      }
      if ((await Notification.requestPermission()) !== "granted") return;
      const { data } = await axios.get(`${API}/push/vapid-key`);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON(), lat: pos?.lat, lng: pos?.lng, radius }, { withCredentials: true });
      setPushOn(true);
      dismissNudge();
    } catch { /* user blocked or unsupported */ }
  };

  // 🔥 Deal-drop ping radius — how far away a deal can be and still buzz this browser.
  const setPushRadius = async (mi) => {
    setRadius(mi);
    localStorage.setItem("airpog_push_radius", String(mi));
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON(), lat: pos?.lat, lng: pos?.lng, radius: mi }, { withCredentials: true });
    } catch { /* offline — saved locally, applied on next subscribe */ }
  };

  useEffect(() => {
    if (!open) return;
    const pull = () => axios.get(`${API}/slammer/live-count`).then((r) => setSlamNow(r.data.now)).catch(() => {});
    pull();
    const t = setInterval(pull, 15000);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/board`, { withCredentials: true }).then((r) => {
      setPosts(r.data.posts || []);
      setMyRsvps(new Set(r.data.my_rsvps || []));
      if (r.data.latest_ts) try { localStorage.setItem(BOARD_SEEN_KEY, r.data.latest_ts); } catch { /* ignore */ }
    }).catch(() => setPosts([]));
  }, [open]);

  const rsvp = (postId) => {
    axios.post(`${API}/board/${postId}/rsvp`, {}, { withCredentials: true }).then((r) => {
      setMyRsvps((s) => { const n = new Set(s); r.data.going ? n.add(postId) : n.delete(postId); return n; });
      setPosts((ps) => ps.map((p) => (p.id === postId ? { ...p, rsvp_count: r.data.count } : p)));
    }).catch(() => {});
  };

  const [sharedId, setSharedId] = useState(null);
  const share = async (p) => {
    const url = `${window.location.origin}/?scroll=1`;
    const text = `📜 ${p.title} — on the AIR.POG SCROLL`;
    try {
      if (navigator.share) { await navigator.share({ title: p.title, text, url }); return; }
    } catch { return; }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setSharedId(p.id);
      setTimeout(() => setSharedId(null), 1600);
    } catch { /* ignore */ }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[2100] bg-black/60 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        >
          <motion.div
            data-testid="pog-board-sheet"
            initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 80 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong w-full sm:max-w-lg h-[80vh] sm:h-[74vh] rounded-t-3xl sm:rounded-3xl border border-sky-400/25 flex flex-col overflow-hidden"
          >
            <div className="px-4 pt-4 pb-3 border-b border-slate-700/60 shrink-0">
              <div className="flex items-center justify-between">
                <p className="font-heading font-black text-lg text-white flex items-center gap-2">
                  <Scroll className="w-5 h-5 text-sky-400" /> THE POG SCROLL
                </p>
                <span className="flex items-center gap-1.5">
                  {pushSupported && (
                    <button
                      data-testid="pog-board-push-toggle"
                      onClick={togglePush}
                      title={pushOn ? "Alerts ON — tap to turn off" : "Get notified of new SCROLL posts"}
                      className={`h-9 px-2.5 rounded-xl flex items-center gap-1.5 text-[10px] font-heading font-black transition-colors ${pushOn ? "bg-amber-400 text-slate-900" : "bg-slate-800/70 text-amber-300 hover:bg-slate-700 border border-amber-400/30"}`}
                    >
                      {pushOn ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                      {pushOn ? "ALERTS ON" : "GET ALERTS"}
                    </button>
                  )}
                  <button data-testid="pog-board-close" onClick={onClose} className="w-9 h-9 rounded-xl bg-slate-800/70 hover:bg-slate-700 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-300" />
                  </button>
                </span>
              </div>
              <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Notifications & Events from AIR.POG HQ — for every merchant & POG'r. 🔔📜</p>
              {pushOn && (
                <div data-testid="push-radius-row" className="flex items-center gap-2 mt-2.5">
                  <p className="text-[11px] font-bold text-slate-400 flex-1">🔥 Deal-drop pings within:</p>
                  {[5, 10, 25].map((mi) => (
                    <button key={mi} data-testid={`push-radius-${mi}`} onClick={() => setPushRadius(mi)}
                      className={`h-7 px-2.5 rounded-lg text-[10px] font-heading font-black transition-colors ${radius === mi ? "bg-amber-400 text-slate-900" : "bg-slate-800/70 text-amber-300 border border-amber-400/30 hover:bg-slate-700"}`}>
                      {mi} mi
                    </button>
                  ))}
                </div>
              )}
              {nudge && !pushOn && (
                <div data-testid="alert-nudge" className="flex items-center gap-2 bg-amber-500/10 border border-amber-400/40 rounded-xl px-3 py-2 mt-2.5">
                  <p className="text-[11px] font-bold text-amber-200 flex-1">🥞 POG DAY is coming — turn on alerts so you never miss a drop!</p>
                  <button data-testid="alert-nudge-on" onClick={togglePush} className="h-7 px-2.5 rounded-lg bg-amber-400 text-slate-900 text-[10px] font-heading font-black shrink-0">TURN ON</button>
                  <button data-testid="alert-nudge-dismiss" onClick={dismissNudge} className="w-7 h-7 rounded-lg bg-slate-800/70 text-slate-400 text-xs font-black shrink-0">✕</button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
              {posts === null && <p className="text-sm text-slate-500 text-center pt-8">Unrolling the scroll…</p>}
              {posts && posts.length === 0 && (
                <div className="text-center pt-12">
                  <Scroll className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm font-bold text-slate-400">The scroll is blank… for now.</p>
                  <p className="text-[11px] text-slate-500 mt-1">HQ announcements land here. POG AWAY! 🚀</p>
                </div>
              )}
              {posts && posts.map((p) => <BoardPost key={p.id} p={p} going={myRsvps.has(p.id)} onRsvp={rsvp} canRsvp={!!driver} onShare={share} shared={sharedId === p.id} slamNow={slamNow} />)}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
